"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCircleX, IconShieldExclamation } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { analytics } from "@/lib/analytics/client";
import { parseRetryAfterHeader } from "@/lib/ratelimit/client";
import type { ScreenshotData } from "@domainstack/types";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
const POLL_INTERVAL_MS = 2000;
const POLL_RECOVERY_INTERVAL_MS = 5000;
type ScreenshotQueryState = {
  status: "completed";
  source: "cache" | "workflow";
  data: ScreenshotData;
} | {
  status: "running";
  runId: string;
} | {
  status: "failed";
  error: string;
} | {
  status: "rate_limited";
  retryAfter: number;
  runId?: string;
};
type TerminalScreenshotQueryState = Extract<ScreenshotQueryState, {
  status: "completed" | "failed";
}>;
function getScreenshotQueryKey(domain: string, domainId?: string) {
  return ["screenshot", domainId ?? domain] as const;
}
function parseScreenshotData(raw: unknown): ScreenshotData {
  if (!raw || typeof raw !== "object") {
    throw new Error("Screenshot response is missing data");
  }
  const data = raw as Record<string, unknown>;
  return {
    url: typeof data.url === "string" ? data.url : null,
    blocked: data.blocked === true
  };
}
function parseStartResponse(raw: unknown): ScreenshotQueryState {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid screenshot response");
  }
  const obj = raw as Record<string, unknown>;
  if ("error" in obj && !("status" in obj)) {
    throw new Error(typeof obj.error === "string" ? obj.error : "Screenshot request failed");
  }
  if (obj.status === "running" && typeof obj.runId === "string") {
    return {
      status: "running",
      runId: obj.runId
    };
  }
  if (obj.status === "completed" && obj.success === false) {
    return {
      status: "failed",
      error: typeof obj.error === "string" ? obj.error : "Screenshot capture failed"
    };
  }
  if (obj.status === "completed" && obj.data) {
    return {
      status: "completed",
      source: "cache",
      data: parseScreenshotData(obj.data)
    };
  }
  throw new Error("Unknown screenshot response format");
}
function parseStatusResponse(raw: unknown, runId: string): ScreenshotQueryState {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid screenshot status response");
  }
  const obj = raw as Record<string, unknown>;
  if ("error" in obj && !("status" in obj)) {
    throw new Error(typeof obj.error === "string" ? obj.error : "Screenshot status unavailable");
  }
  if (obj.status === "running") {
    return {
      status: "running",
      runId
    };
  }
  if (obj.status === "failed") {
    return {
      status: "failed",
      error: typeof obj.error === "string" ? obj.error : "Workflow failed"
    };
  }
  if (obj.status === "completed" && obj.data) {
    return {
      status: "completed",
      source: "workflow",
      data: parseScreenshotData(obj.data)
    };
  }
  throw new Error("Unknown screenshot status response format");
}
async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const raw = (await response.json()) as {
      error?: unknown;
    };
    return typeof raw.error === "string" ? raw.error : fallback;
  } catch {
    return fallback;
  }
}
async function startScreenshot(domainId: string): Promise<ScreenshotQueryState> {
  const response = await fetch("/api/screenshot", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      domainId
    })
  });
  if (response.status === 429) {
    return {
      status: "rate_limited",
      retryAfter: parseRetryAfterHeader(response)
    };
  }
  if (!response.ok) {
    const error = await readErrorMessage(response, `Screenshot request failed: ${response.status}`);
    if (response.status >= 400 && response.status < 500) {
      return {
        status: "failed",
        error
      };
    }
    throw new Error(error);
  }
  return parseStartResponse(await response.json());
}
async function pollScreenshot(runId: string): Promise<ScreenshotQueryState> {
  const response = await fetch(`/api/screenshot?runId=${encodeURIComponent(runId)}`, {
    cache: "no-store"
  });
  if (response.status === 429) {
    return {
      status: "rate_limited",
      retryAfter: parseRetryAfterHeader(response),
      runId
    };
  }
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, `Screenshot status poll failed: ${response.status}`));
  }
  return parseStatusResponse(await response.json(), runId);
}
function isTerminalState(state: ScreenshotQueryState | undefined): state is TerminalScreenshotQueryState {
  return state?.status === "completed" || state?.status === "failed";
}
export interface UseScreenshotResult {
  data: ScreenshotData | null;
  isLoading: boolean;
  error: Error | null;
  hasFailed: boolean;
}
function ScreenshotPlaceholder(t0) {
  const $ = _c(7);
  const {
    isLoading,
    blocked,
    aspectClassName,
    onReload
  } = t0;
  const t1 = `h-auto w-full ${aspectClassName} flex items-center justify-center bg-muted/50`;
  let t2;
  if ($[0] !== blocked || $[1] !== isLoading || $[2] !== onReload) {
    t2 = <div className="flex items-center gap-2 text-xs text-muted-foreground [&_svg]:size-4" aria-live="polite">{isLoading ? <><Spinner />Taking screenshot…</> : blocked ? <><IconShieldExclamation />Screenshot unavailable for this domain.</> : <><IconCircleX /><span>Unable to take a screenshot.</span>{onReload ? <button type="button" className="min-h-6 rounded-sm px-1.5 font-medium text-foreground underline underline-offset-2 hover:text-foreground/80 focus-visible:outline-2 focus-visible:outline-offset-2" onClick={onReload}>Reload preview</button> : null}</>}</div>;
    $[0] = blocked;
    $[1] = isLoading;
    $[2] = onReload;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function withReloadToken(url: string, reloadCount: number): string {
  if (reloadCount === 0) return url;
  try {
    const reloadUrl = new URL(url);
    reloadUrl.searchParams.set("domainstack-reload", String(reloadCount));
    return reloadUrl.toString();
  } catch {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}domainstack-reload=${reloadCount}`;
  }
}
function ScreenshotImage(t0) {
  const $ = _c(20);
  const {
    domain,
    url,
    width,
    height,
    imageClassName,
    aspectClassName
  } = t0;
  const [reloadCount, setReloadCount] = useState(0);
  const [hasFailed, setHasFailed] = useState(false);
  if (hasFailed) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = () => {
        setReloadCount(_temp);
        setHasFailed(false);
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== aspectClassName) {
      t2 = <ScreenshotPlaceholder isLoading={false} blocked={false} aspectClassName={aspectClassName} onReload={t1} />;
      $[1] = aspectClassName;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const t1 = `https://${domain}`;
  let t2;
  if ($[3] !== reloadCount || $[4] !== url) {
    t2 = withReloadToken(url, reloadCount);
    $[3] = reloadCount;
    $[4] = url;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `Homepage preview of ${domain}`;
  let t4;
  if ($[6] !== aspectClassName || $[7] !== imageClassName) {
    t4 = cn("h-auto w-full object-cover", aspectClassName, imageClassName);
    $[6] = aspectClassName;
    $[7] = imageClassName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setHasFailed(true);
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== height || $[11] !== reloadCount || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== width) {
    t6 = <Image key={reloadCount} src={t2} alt={t3} width={width} height={height} className={t4} unoptimized={true} priority={false} draggable={false} onError={t5} />;
    $[10] = height;
    $[11] = reloadCount;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = width;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t1 || $[18] !== t6) {
    t7 = <a href={t1} target="_blank" rel="noopener">{t6}</a>;
    $[17] = t1;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

/**
 * Hook to fetch a screenshot for a domain.
 * Call this in a component that stays mounted to keep polling active.
 */
function _temp(current) {
  return current + 1;
}
export function useScreenshot(t0) {
  const $ = _c(29);
  const {
    domain,
    domainId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[0] !== domain || $[1] !== domainId) {
    t2 = getScreenshotQueryKey(domain, domainId);
    $[0] = domain;
    $[1] = domainId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const queryKey = t2;
  let t3;
  if ($[3] !== domainId || $[4] !== queryClient || $[5] !== queryKey) {
    t3 = async () => {
      const current = queryClient.getQueryData(queryKey);
      if (current?.status === "running") {
        return pollScreenshot(current.runId);
      }
      if (current?.status === "rate_limited" && current.runId) {
        return pollScreenshot(current.runId);
      }
      if (isTerminalState(current)) {
        return current;
      }
      if (!domainId) {
        throw new Error("Screenshot domain ID is missing");
      }
      return startScreenshot(domainId);
    };
    $[3] = domainId;
    $[4] = queryClient;
    $[5] = queryKey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = enabled && !!domainId;
  let t5;
  if ($[7] !== queryKey || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey,
      queryFn: t3,
      enabled: t4,
      retry: false,
      staleTime: _temp2,
      refetchOnMount: _temp3,
      refetchInterval: _temp4,
      refetchIntervalInBackground: true
    };
    $[7] = queryKey;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const screenshotQuery = useQuery(t5);
  const reportedStateRef = useRef(null);
  let t6;
  let t7;
  if ($[11] !== domain || $[12] !== domainId || $[13] !== screenshotQuery.data) {
    t6 = () => {
      const state_0 = screenshotQuery.data;
      if (!state_0) {
        return;
      }
      const marker = state_0.status === "running" ? `running:${state_0.runId}` : state_0.status === "completed" ? `completed:${state_0.source}:${state_0.data.url ?? "none"}` : state_0.status === "rate_limited" ? `rate-limited:${state_0.runId ?? "start"}:${state_0.retryAfter}` : `failed:${state_0.error}`;
      if (reportedStateRef.current === marker) {
        return;
      }
      reportedStateRef.current = marker;
      if (state_0.status === "running") {
        analytics.track("screenshot_requested", {
          domain
        });
      } else {
        if (state_0.status === "completed") {
          analytics.track(state_0.source === "cache" ? "screenshot_loaded_from_cache" : "screenshot_loaded_from_api", {
            domain
          });
        } else {
          if (state_0.status === "rate_limited") {
            toast.error("Too many requests", {
              id: `screenshot-rate-limited-${domainId ?? domain}`,
              description: `Retrying in ${state_0.retryAfter} second${state_0.retryAfter !== 1 ? "s" : ""}.`
            });
            analytics.track("screenshot_rate_limited", {
              domain,
              retryAfter: state_0.retryAfter
            });
          }
        }
      }
    };
    t7 = [screenshotQuery.data, domain, domainId];
    $[11] = domain;
    $[12] = domainId;
    $[13] = screenshotQuery.data;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[16] !== domain || $[17] !== screenshotQuery.error) {
    t8 = () => {
      if (screenshotQuery.error) {
        analytics.trackException(screenshotQuery.error, {
          domain
        });
      }
    };
    t9 = [screenshotQuery.error, domain];
    $[16] = domain;
    $[17] = screenshotQuery.error;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  const state_1 = screenshotQuery.data;
  const data = state_1?.status === "completed" ? state_1.data : null;
  const hasFailed = state_1?.status === "failed";
  let t10;
  if ($[20] !== hasFailed || $[21] !== screenshotQuery.error || $[22] !== state_1) {
    t10 = hasFailed ? new Error(state_1.error) : screenshotQuery.error ?? null;
    $[20] = hasFailed;
    $[21] = screenshotQuery.error;
    $[22] = state_1;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const error = t10;
  const isLoading = enabled && (!domainId || !data && !hasFailed);
  let t11;
  if ($[24] !== data || $[25] !== error || $[26] !== hasFailed || $[27] !== isLoading) {
    t11 = {
      data,
      isLoading,
      error,
      hasFailed
    };
    $[24] = data;
    $[25] = error;
    $[26] = hasFailed;
    $[27] = isLoading;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}

/**
 * Display component for screenshot results.
 * Use with useScreenshot hook for data fetching.
 */
function _temp4(query_1) {
  const state = query_1.state.data;
  if (isTerminalState(state)) {
    return false;
  }
  if (state?.status === "rate_limited") {
    return state.retryAfter * 1000;
  }
  return state?.status === "running" ? POLL_INTERVAL_MS : POLL_RECOVERY_INTERVAL_MS;
}
function _temp3(query_0) {
  return !isTerminalState(query_0.state.data);
}
function _temp2(query) {
  return isTerminalState(query.state.data) ? Number.POSITIVE_INFINITY : 0;
}
export function Screenshot(t0) {
  const $ = _c(12);
  const {
    domain,
    data,
    isLoading,
    className,
    width: t1,
    height: t2,
    imageClassName,
    aspectClassName: t3
  } = t0;
  const width = t1 === undefined ? 1200 : t1;
  const height = t2 === undefined ? 630 : t2;
  const aspectClassName = t3 === undefined ? "aspect-[1200/630]" : t3;
  const url = data?.url ?? null;
  const blocked = data?.blocked ?? false;
  let t4;
  if ($[0] !== aspectClassName || $[1] !== blocked || $[2] !== domain || $[3] !== height || $[4] !== imageClassName || $[5] !== isLoading || $[6] !== url || $[7] !== width) {
    t4 = url ? <ScreenshotImage key={url} domain={domain} url={url} width={width} height={height} imageClassName={imageClassName} aspectClassName={aspectClassName} /> : <ScreenshotPlaceholder isLoading={isLoading} blocked={blocked} aspectClassName={aspectClassName} />;
    $[0] = aspectClassName;
    $[1] = blocked;
    $[2] = domain;
    $[3] = height;
    $[4] = imageClassName;
    $[5] = isLoading;
    $[6] = url;
    $[7] = width;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== className || $[10] !== t4) {
    t5 = <div className={className}>{t4}</div>;
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}